import { MouseEvent, useRef } from "react";
import { Box, BoxProps } from "@mui/material";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { IPortfolioChartDict } from "src/api/server/wf-detail.types";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { useJumpMarksNavigation } from "src/components-mui/common/jump-marks/jump-marks.context";
import { useIsSectionInView } from "src/components-mui/common/jump-marks/use-is-section-in-view";
import { DetailJumpMark } from "src/components-mui/pages/detail/detail-jump-marks";
import { LoadingState } from "src/components-mui/pages/detail/portfolio-chart/loading-state";
import type { IPortfolioChartProps } from "src/components-mui/pages/detail/portfolio-chart/portfolio-chart";

interface IPortfolioChartWrapper extends Pick<BoxProps, "sx"> {
    wikifolioId: string;
    dict: IPortfolioChartDict;
}

const PortfolioChart = dynamic<IPortfolioChartProps>(
    () => import("src/components-mui/pages/detail/portfolio-chart/portfolio-chart").then(component => component.PortfolioChart),
    {
        loading: () => <LoadingState />,
    }
);

export const PortfolioChartWrapper = ({ wikifolioId, dict, sx }: IPortfolioChartWrapper) => {
    const { t } = useTranslation("common");
    const sectionRef = useRef<HTMLDivElement>(null);
    const isVisible = useIsSectionInView({ sectionRef, visibleOnce: true });
    const { navigateToSection } = useJumpMarksNavigation();

    const handleDetailsClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        navigateToSection(DetailJumpMark.Portfolio);
    };

    return (
        <Box ref={sectionRef} component="section" sx={[{ position: "relative" }, ...(Array.isArray(sx) ? sx : [sx])]}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <WfHeading component="h2" fontSize="2xl">
                    {dict.title}
                </WfHeading>
                <ArrowLink href={`#${DetailJumpMark.Portfolio}`} onClick={handleDetailsClick} sx={{ fontSize: "md" }}>
                    {t("details")}
                </ArrowLink>
            </Box>
            {isVisible ? <PortfolioChart wikifolioId={wikifolioId} dict={dict} /> : <LoadingState />}
        </Box>
    );
};
